import {
  processApiFieldErrors,
  type ProcessApiFieldErrorsOptions,
} from '../../../utils/processApiFieldErrors'
// getErrorMessage lives in utils.js; TS imports it fine (allowJs: true). Used as
// the no-field-error fallback.
import { getErrorMessage } from '../../../utils/utils'

export interface ApplyPlayerApiErrorDeps extends ProcessApiFieldErrorsOptions {
  /** Apply Formik field errors (Formik `setErrors`). */
  setErrors: (errors: Record<string, string>) => void
  /**
   * Mark the errored fields as touched (Formik `setTouched`). Optional because
   * some platforms render errors off `meta.error` alone, but web gates its error
   * UI on `meta.touched && meta.error` (matching submit-time behavior, where
   * Formik touches every field). Without this, API-set field errors on web would
   * set the message but show no red field layout. Receives a `{ field: true }`
   * map for exactly the fields that errored.
   */
  setTouched?: (touched: Record<string, boolean>) => void
  /** Surface the summary toast (web: message.error; mobile: Alert.alert). */
  notifyError: (message: string) => void
  /**
   * Called (after field errors are applied) with the errored form-field keys, so
   * a platform can scroll/focus the first errored field into view. Optional —
   * fires only in the field-error branch, never for the toast fallback.
   */
  onFieldErrors?: (fields: string[]) => void
}

/**
 * Translate a thrown player-save API error into Formik field errors. Field
 * errors highlight the inputs inline (mirroring client-side required-field
 * behavior); no summary toast is shown for field-error responses — the inline
 * per-field messages are the surface. Falls back to a toast (backend `.message`,
 * then getErrorMessage) only when the error carries no mapped `invalid_fields`.
 */
export const applyPlayerApiError = (
  err: unknown,
  deps: ApplyPlayerApiErrorDeps
): void => {
  const {
    fieldMap,
    titleMap,
    setErrors,
    setTouched,
    notifyError,
    onFieldErrors,
  } = deps
  const e = err as {
    invalid_fields?: Record<string, string | string[]>
    message?: string
  }

  const { formErrors, titles, hasFieldErrors } = processApiFieldErrors(
    e.invalid_fields,
    { fieldMap, titleMap }
  )

  if (!hasFieldErrors || titles.length === 0) {
    // When there are no field errors at all, or when every invalid_fields key is
    // unmapped (nothing to highlight in the form), prefer the backend's own
    // .message string. Fall back to getErrorMessage for errors that carry no
    // .message (e.g. network errors, plain Error instances).
    notifyError(e.message ?? getErrorMessage(err))
    return
  }

  setErrors(formErrors)
  // Touch the errored fields so platforms that gate their error UI on
  // `touched && error` (web) actually render the red field layout.
  setTouched?.(
    Object.keys(formErrors).reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = true
      return acc
    }, {})
  )
  // No toast: the inline per-field errors are the only surface for field errors.
  // Let the platform bring the first errored field into view (web scroll+focus).
  onFieldErrors?.(Object.keys(formErrors))
}
